// src/components/wellfinder/holderWording.js
//
// What a taken candidate says about the record that holds its registry well.
//
// ── "MATCHED" IS A CLAIM ABOUT A PERSON'S JUDGMENT ───────────────────────────
// The old label said "already matched" for every holder. Measured 2026-09-26,
// 5,137 of 9,922 holders are pending records no analyst has reviewed — rank-1
// wells handed out at assignment. Calling those "matched" tells the analyst a
// colleague has already decided, when nobody has; it is the difference
// between deferring to a verdict and questioning an automatic guess.
//
// So the verb follows the holder's status:
//   approved      matched to <id>   — an analyst's verdict, with who and when
//   pending       assigned to <id>  — not yet reviewed (or reopened and not
//                                     yet re-reviewed: reopen keeps the well)
//   anything else held by <id>      — rejected or flagged, named as such
//
// Adopting is refused in every case; that part is the caller's and unchanged.

const TIER_LABEL = { a: "A", a_prime: "A′", b: "B", c: "C", d: "D" };

const OTHER_STATUS = { rejected: "rejected", needs_review: "flagged for review" };

// US English, date only. The time of a colleague's verdict is not what the
// analyst is weighing; that it happened, and roughly when, is.
function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** The id the holding record's own header shows, so the two can be matched by eye. */
export function holderId(c) {
  return c.taken_by_detection_source_id ?? c.taken_by_source_id ?? "another record";
}

/**
 * { kind, verb, id, detail } for a taken candidate, or null for a free one.
 * `detail` is the parenthetical after the id — tier, and who/when or why.
 */
export function holderWording(c) {
  if (!c?.is_taken) return null;
  const tier = TIER_LABEL[c.taken_by_tier];
  const parts = tier ? [`tier ${tier}`] : [];
  const id = holderId(c);

  if (c.taken_by_status === "approved") {
    const who = [c.taken_by_reviewed_by_name, fmtDate(c.taken_by_reviewed_at)]
      .filter(Boolean).join(", ");
    if (who) parts.push(who);
    return { kind: "matched", verb: "matched to", id, detail: parts.join(" · ") };
  }
  if (c.taken_by_status === "pending") {
    parts.push(c.taken_by_reopened ? "reopened, not yet re-reviewed" : "not yet reviewed");
    return { kind: "assigned", verb: "assigned to", id, detail: parts.join(" · ") };
  }
  parts.push(OTHER_STATUS[c.taken_by_status] ?? c.taken_by_status ?? "status unknown");
  return { kind: "held", verb: "held by", id, detail: parts.join(" · ") };
}
